import { FilterListRounded } from "@mui/icons-material";
import { Badge, Button, IconButton, Menu, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { MetricsFilterByOption } from "src/constants";
import { useGetMetricCollection } from "src/http";
import { ToggleButtonSubmenu } from "../ToggleButtonSubmenu";

interface Props {
  variant: "text" | "icon";
  metricCollectionGuid: string;
  filters: Record<MetricsFilterByOption, string[]>;
  onFiltersChanged: (filters: Record<MetricsFilterByOption, string[]>) => void;
}

const MetricsCollectionFilterButton = (props: Props) => {
  const { data: metrics, isLoading } = useGetMetricCollection(props.metricCollectionGuid);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  function handleFilterByOptionChanged(options: string[]) {
    // Ignore other options that require a value selection from a submenu
    if (options.includes("All")) {
      props.onFiltersChanged({
        [MetricsFilterByOption.ReportingPerson]: [],
        [MetricsFilterByOption.Tag]: [],
        [MetricsFilterByOption.Team]: [],
      });
    }
  }

  function handleFilterByValueChanged(option: MetricsFilterByOption, value: string[]) {
    if (value != null) {
      props.onFiltersChanged({
        ...props.filters,
        [option]: value,
      });
    }
  }

  const teamOptions = _.uniq(metrics?.map((metric) => metric.assignedTeam?.name ?? "None") ?? []).sort(
    filterOptionSort,
  );

  const reportingUserOptions = _.uniq(metrics?.map((metric) => metric.reportingUser?.displayName ?? "None") ?? []).sort(
    filterOptionSort,
  );

  const tagOptions = _.uniq(metrics?.flatMap((metric) => metric.tags) ?? []).sort(filterOptionSort);

  function filterOptionSort(a: string, b: string) {
    if (a === "None") {
      return -1;
    }

    if (b === "None") {
      return 1;
    }

    return a.localeCompare(b);
  }

  const appliedFilters = [
    MetricsFilterByOption.Team,
    MetricsFilterByOption.ReportingPerson,
    MetricsFilterByOption.Tag,
  ].filter((x) => {
    return props.filters[x].length > 0;
  });

  const appliedFiltersCount = appliedFilters.reduce((sum, option) => {
    return sum + props.filters[option].length;
  }, 0);

  return (
    <>
      <Badge color="primary" variant="dot" invisible={appliedFilters.length === 0}>
        {props.variant === "text" && (
          <Button
            variant="tertiary"
            disabled={isLoading}
            startIcon={<FilterListRounded />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            {appliedFilters.length === 0
              ? `Filter`
              : appliedFiltersCount > 1
                ? `${appliedFiltersCount} Filters`
                : `1 Filter`}
          </Button>
        )}

        {props.variant === "icon" && (
          <IconButton variant="tertiary" disabled={isLoading} onClick={(e) => setMenuAnchor(e.currentTarget)}>
            <FilterListRounded />
          </IconButton>
        )}
      </Badge>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
      >
        <ToggleButtonGroup
          value={appliedFilters.length > 0 ? appliedFilters : ["All"]}
          orientation="vertical"
          variant="menu"
          onChange={(_, option) => handleFilterByOptionChanged(option)}
          sx={{ minWidth: "100%" }}
        >
          <ToggleButton value="All">
            <Typography>All</Typography>
          </ToggleButton>

          <ToggleButtonSubmenu
            disabled={teamOptions.length <= 1}
            label={MetricsFilterByOption.Team}
            value={MetricsFilterByOption.Team}
            suboptions={teamOptions}
            selectedSuboptionValues={props.filters[MetricsFilterByOption.Team]}
            onSelected={(suboptions) => handleFilterByValueChanged(MetricsFilterByOption.Team, suboptions)}
          />

          <ToggleButtonSubmenu
            disabled={reportingUserOptions.length <= 1}
            label={MetricsFilterByOption.ReportingPerson}
            value={MetricsFilterByOption.ReportingPerson}
            suboptions={reportingUserOptions}
            selectedSuboptionValues={props.filters[MetricsFilterByOption.ReportingPerson]}
            onSelected={(suboptions) => handleFilterByValueChanged(MetricsFilterByOption.ReportingPerson, suboptions)}
          />

          <ToggleButtonSubmenu
            disabled={tagOptions.length <= 1}
            label={MetricsFilterByOption.Tag}
            value={MetricsFilterByOption.Tag}
            suboptions={tagOptions}
            selectedSuboptionValues={props.filters[MetricsFilterByOption.Tag]}
            onSelected={(suboptions) => handleFilterByValueChanged(MetricsFilterByOption.Tag, suboptions)}
          />
        </ToggleButtonGroup>
      </Menu>
    </>
  );
};

export { MetricsCollectionFilterButton };
